import React from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"
import { useMediaQuery } from "react-responsive"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleLeft } from "@fortawesome/free-solid-svg-icons"
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons"
import logo from "../../assets/images/Logo.png"
import { PATH_MAP } from "../../configuration/PathMap"
import { defaultMarketingQuizSlug } from "../../configuration/MarketingQuizzes"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { trackMarketingEvent } from "../../analytics/tracking"
import { usePublicContact } from "../../context/usePublicContactContext"
import { useSignUpContext } from "../../context/useSignUpContext"
import { SignUp } from "../../Objects/SignUp/SignUp"
import ModalPortal from "../Modal/ModalPortal"
import floatButtonStyle from "../FloatButton/FloatButton.module.css"

interface NavbarProps {
  showFunctions?: boolean
  showReturn?: boolean
  showCreateAccountButton?: boolean
  showWhatsAppFloat?: boolean
  fallbackPath?: string
}

const Navbar: React.FC<NavbarProps> = ({
  showFunctions = true,
  showReturn = false,
  showCreateAccountButton = false,
  showWhatsAppFloat = true,
  fallbackPath = PATH_MAP.ROOT_PAGE,
}) => {
  const { t } = useTranslation("landpage")

  const navigate = useNavigate()
  const [canGoBack, setCanGoBack] = React.useState(false)

  const { contact } = usePublicContact()
  const { updateSignUp } = useSignUpContext()

  const isDesktop = useMediaQuery({ query: "(min-width: 1024px)" })

  React.useEffect(() => {
    if (!showReturn) {
      setCanGoBack(false)
      return
    }

    try {
      const historyState = window.history.state as { idx?: number } | null

      if (typeof historyState?.idx === "number") {
        setCanGoBack(historyState.idx > 0)
        return
      }

      setCanGoBack(window.history.length > 1)
    } catch {
      setCanGoBack(false)
    }
  }, [showReturn])

  const handleGoBack = () => {
    if (canGoBack) {
      navigate(-1)
      return
    }

    navigate(fallbackPath, { replace: true })
  }

  const handleCreateAccount = (target: string) => {
    trackMarketingEvent("landing_nav_click", { target })
    updateSignUp(new SignUp())
    navigate(PATH_MAP.ROOT_SIGN_UP)
  }

  return (
    <>
      <ModalPortal>
      <Animate type={AnimationTypes.slideDownIn}>
        <nav
          className="
          fixed top-0 left-0 right-0 z-[100]
          transition-all duration-300 ease-in-out
          py-4
          bg-white/80 backdrop-blur-xl
          shadow-sm
          "
        >
        <div
          className="
          max-w-[1280px]
          mx-auto
          px-6
          flex
          justify-between
          items-center
        "
        >
          <div className="flex items-center gap-3">
            {showReturn && (
              <button
                type="button"
                onClick={handleGoBack}
                className="mr-3 text-[30px] cursor-pointer transition-transform duration-300 hover:scale-125"
                aria-label={t("label_back_aria")}
                data-interaction-description-key="interactionDescriptions:components.landPage.navbar.button.backAria"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
            )}

            <div className="cursor-pointer">
              <Animate type={AnimationTypes.fadeIn} duration="2s">
                <img
                  src={logo}
                  className="w-[90px] max-[480px]:w-[100px]"
                  alt="Logo"
                  onClick={() => {
                    trackMarketingEvent("landing_nav_click", { target: "home_logo" })
                    navigate(PATH_MAP.ROOT_PAGE)
                  }}
                  data-interaction-description-key="interactionDescriptions:components.landPage.navbar.button.navigatePATHMAPROOTPAGE"
                />
              </Animate>
            </div>
          </div>

          {showFunctions && (
            <div className="hidden lg:flex flex-col items-end gap-2 mr-10">
              <div className="flex flex-wrap justify-end gap-6">
                <button
                  onClick={() => navigate(PATH_MAP.HOW_IT_WORKS_PAGE)}
                  className="
                  cursor-pointer
                  text-sm font-semibold
                  text-slate-600
                  transition-colors duration-200
                  hover:text-red-600
                "
                  data-interaction-description-key="interactionDescriptions:components.landPage.navbar.button.navigatePATHMAPHOWITWORKSPAGE"
                >
                  {t("label_how_it_works")}
                </button>

                <button
                  onClick={() => navigate(PATH_MAP.PUBLIC_TUTORIALS_PAGE)}
                  className="
                  cursor-pointer
                  text-sm font-semibold
                  text-slate-600
                  transition-colors duration-200
                  hover:text-red-600
                "
                  data-interaction-description-key="interactionDescriptions:components.landPage.navbar.button.navigatePATHMAPPUBLICTUTORIALSPAGE"
                >
                  {t("label_tutorials")}
                </button>

                <button
                  onClick={() => navigate(PATH_MAP.LEAD_QUIZ_LINK(defaultMarketingQuizSlug))}
                  className="
                  cursor-pointer
                  text-sm font-semibold
                  text-slate-600
                  transition-colors duration-200
                  hover:text-red-600
                "
                  data-interaction-description-key="interactionDescriptions:components.landPage.navbar.button.navigatePATHMAPLEADQUIZPAGE"
                >
                  {t("label_public_menu")}
                </button>
              </div>

              <div className="hidden xl:flex flex-wrap justify-end gap-5">
                <a
                  href="#features"
                  className="
                  text-xs font-bold
                  text-slate-500
                  transition-colors duration-200
                  hover:text-red-600
                "
                  data-interaction-description-key="interactionDescriptions:components.landPage.navbar.link.functions"
                >
                  {t("label_functions")}
                </a>

                <a
                  href="#plans"
                  className="
                  text-xs font-bold
                  text-slate-500
                  transition-colors duration-200
                  hover:text-red-600
                "
                  data-interaction-description-key="interactionDescriptions:components.landPage.navbar.link.plans"
                >
                  {t("label_plans")}
                </a>

                <Link
                  to={PATH_MAP.RESELLER_LANDING_PAGE}
                  className="
                  text-xs font-bold
                  text-slate-500
                  transition-colors duration-200
                  hover:text-red-600
                "
                  data-interaction-description-key="interactionDescriptions:components.landPage.navbar.link.reseller"
                >
                  {t("label_be_reseller")}
                </Link>
              </div>
            </div>
          )}

          {showCreateAccountButton && (
            <button
              type="button"
              onClick={() => handleCreateAccount("navbar_create_account")}
              className="
              inline-flex shrink-0 items-center justify-center
              cursor-pointer
              lg:hidden
              rounded-full
              bg-gradient-to-r from-red-600 to-orange-500
              px-4 py-2
              text-xs font-black text-white
              whitespace-nowrap
              shadow-md shadow-red-500/20
              transition-all duration-300
              hover:-translate-y-0.5 hover:shadow-lg hover:shadow-orange-400/30
              active:scale-95
              max-[480px]:px-3 max-[480px]:py-1.5 max-[480px]:text-[11px]
              "
              data-interaction-description-key="interactionDescriptions:components.landPage.navbar.button.handleCreateAccountNavbarCreateAccount"
            >
              {t("button_create_account")}
            </button>
          )}
        </div>
      </nav>
      </Animate>
      </ModalPortal>

      {showWhatsAppFloat && contact.whatsappUrl && (
        <ModalPortal>
          <a
            href={contact.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("label_whatsapp_float_aria")}
            title={t("label_whatsapp_float_aria")}
            onClick={() => trackMarketingEvent("landing_nav_click", { target: "whatsapp_float" })}
            className={floatButtonStyle.floatBottom}
            style={{
              bottom: isDesktop ? 24 : 8,
              right: isDesktop ? 24 : 8,
              width: isDesktop ? 72 : 56,
              height: isDesktop ? 72 : 56,
              borderRadius: "50%",
              background: "#25D366",
              color: "#ffffff",
              fontSize: 26,
            }}
            data-interaction-description-key="interactionDescriptions:components.landPage.navbar.link.whatsAppFloat"
          >
            <FontAwesomeIcon icon={faWhatsapp} />
          </a>
        </ModalPortal>
      )}
    </>
  )
}

export default Navbar
